import React, { useEffect, useMemo } from 'react';
import { Form, InputNumber, Typography } from 'antd';
import { AdminProductFormValues, MaterialVariantStockFieldsProps } from '../../interfaces';

const { Text } = Typography;

const MaterialVariantStockFields: React.FC<MaterialVariantStockFieldsProps> = ({
  materialIndex,
  filteredSizes,
  requiresSizes,
}) => {
  const form = Form.useFormInstance<AdminProductFormValues>();
  const watchedSizeIds = Form.useWatch(['materials', materialIndex, 'sizeIds'], form);
  const selectedSizeKey = useMemo(
    () => Array.isArray(watchedSizeIds) ? watchedSizeIds.join('|') : '',
    [watchedSizeIds]
  );
  const selectedSizeIds = useMemo(
    () => selectedSizeKey ? selectedSizeKey.split('|') : [],
    [selectedSizeKey]
  );
  const selectedSizes = useMemo(
    () => filteredSizes.filter((size) => selectedSizeIds.includes(size.id)),
    [filteredSizes, selectedSizeIds]
  );

  useEffect(() => {
    if (!requiresSizes) {
      return;
    }

    const materials = form.getFieldValue('materials') || [];
    const material = materials[materialIndex] || {};
    const variantStocks = material.variantStocks || {};
    const fallbackStock = Number(material.stockQuantity);
    const nextVariantStocks = selectedSizeIds.reduce<Record<string, number>>((acc, sizeId: string) => {
      const currentStock = Number(variantStocks[sizeId]);

      acc[sizeId] = Number.isFinite(currentStock)
        ? Math.max(0, currentStock)
        : Number.isFinite(fallbackStock)
          ? Math.max(0, fallbackStock)
          : 0;

      return acc;
    }, {});

    form.setFieldsValue({
      materials: materials.map((item: AdminProductFormValues['materials'][number], index: number) =>
        index === materialIndex
          ? { ...item, variantStocks: nextVariantStocks }
          : item
      ),
    });
  }, [form, materialIndex, requiresSizes, selectedSizeIds, selectedSizeKey]);

  if (!requiresSizes) {
    return null;
  }

  return (
    <div className="admin-product-form__variant-create-stock">
      <Text strong>Stock By Size</Text>
      <div className="admin-product-form__variant-create-grid">
        {selectedSizes.map((size) => (
          <Form.Item
            key={size.id}
            name={[materialIndex, 'variantStocks', size.id]}
            label={`${size.label} (${size.value})`}
            rules={[
              { required: true, message: 'Enter stock.' },
              { type: 'number', min: 0, message: 'Stock cannot be negative.' },
            ]}
            className="admin-product-form__variant-create-field"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ))}
      </div>
      {!selectedSizes.length && (
        <Text type="secondary">Select sizes for this material to add stock quantities.</Text>
      )}
    </div>
  );
};

export default MaterialVariantStockFields;
